import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { IconUpload, IconX } from "@tabler/icons-react";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import { cn } from "@/lib/utils";
import { DFY_IMAGE_ACCEPT } from "../dfy-order-form-uploads";

export default function BrandingImageField({
  label,
  imageSrc,
  fit = "cover",
  onUpload,
  onRemove,
}: {
  label: string;
  imageSrc: string | null;
  fit?: "contain" | "cover";
  onUpload: (file: File) => Promise<void>;
  onRemove: () => void;
}) {
  const [isUploading, setIsUploading] = useState(false);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: DFY_IMAGE_ACCEPT,
    multiple: false,
    disabled: isUploading,
    onDrop: async (acceptedFiles) => {
      if (!acceptedFiles[0]) {
        return;
      }
      setIsUploading(true);
      try {
        await onUpload(acceptedFiles[0]);
      } finally {
        setIsUploading(false);
      }
    },
  });

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-medium">{label}</p>
      {imageSrc ? (
        <div className="relative w-full max-w-40">
          <img
            src={imageSrc}
            alt=""
            className={cn(
              "h-28 w-full rounded-lg border bg-white",
              fit === "contain" ? "object-contain p-1" : "object-cover",
            )}
          />
          <button
            type="button"
            aria-label={`Remove ${label.toLowerCase()}`}
            onClick={onRemove}
            className="absolute top-1.5 right-1.5 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full bg-black/60 text-white"
          >
            <IconX className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : isUploading ? (
        <div className="flex h-28 flex-col items-center justify-center rounded-xl border border-dashed border-border bg-file-upload">
          <LoadingSpinner />
          <div className="h-1.5" />
          <p className="text-center text-xs text-text-secondary">
            Uploading...
          </p>
        </div>
      ) : (
        <div
          className={cn(
            "flex h-28 cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed [&_svg]:stroke-[url(#icon-gradient)]",
            isDragActive
              ? "border-primary-400 bg-primary-100"
              : "border-border bg-file-upload",
          )}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          <IconUpload stroke={2} size={20} />
          <div className="h-1.5" />
          <p className="text-center text-xs text-text-secondary">
            Click to upload or drag here
          </p>
        </div>
      )}
    </div>
  );
}
